import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Grid,
} from "@mui/material";
import { Delete, PhotoCamera, AccessTime } from "@mui/icons-material";
import MicrowaveIcon from "@mui/icons-material/Microwave";
import MDEditor from "@uiw/react-md-editor";
import * as commands from "@uiw/react-md-editor/commands";
import "@uiw/react-md-editor/markdown-editor.css";
import apiClient from "../utils/apiClient";

const RecipeStepEditModal = ({ open, onClose, step, onSave, onDelete, recipe, onRecipeRefresh, onImageDeleted }) => {
  const [text, setText] = useState("");
  const [stepNumber, setStepNumber] = useState("");
  const [error, setError] = useState("");
  const lastStepUuidRef = useRef(null); // Track which step we're editing to prevent overwriting user edits

  // Memoize commands to ensure stability across renders (important for production builds)
  const editorCommands = useMemo(() => {
    // Custom command for Time button - wraps selected text in <time> tag
    const timeCommand = {
      name: 'time',
      keyCommand: 'time',
      buttonProps: { 'aria-label': 'Insert time duration', title: 'Insert time duration' },
      icon: <AccessTime style={{ fontSize: 18 }} />,
      execute: (state, api) => {
        const textarea = api.textArea;
        if (!textarea) return;
        
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = state.text.substring(start, end);
        
        // If text is selected, wrap it in <time> tag
        // If no text selected, insert "<time>10 min</time>" as placeholder
        const replacement = selectedText 
          ? `<time>${selectedText}</time>`
          : '<time>10 min</time>';
        
        const newText = state.text.substring(0, start) + replacement + state.text.substring(end);
        
        // Update the text state
        setText(newText);
        
        // Set cursor position after inserted text
        const newCursorPos = start + replacement.length;
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      },
    };

    // Custom command for Oven button - inserts <uses_oven> tag
    const usesOvenCommand = {
      name: 'uses_oven',
      keyCommand: 'uses_oven',
      buttonProps: { 'aria-label': 'Mark step as using oven', title: 'Mark step as using oven' },
      icon: <MicrowaveIcon style={{ fontSize: 18 }} />,
      execute: (state, api) => {
        const textarea = api.textArea;
        if (!textarea) return;
        
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        // Insert <uses_oven> tag at cursor position
        const replacement = '<uses_oven>';
        
        const newText = state.text.substring(0, start) + replacement + state.text.substring(end);
        
        // Update the text state
        setText(newText);
        
        // Set cursor position after inserted tag
        const newCursorPos = start + replacement.length;
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      },
    };

    return [
      commands.bold,
      commands.italic,
      commands.strikethrough,
      commands.hr,
      commands.title,
      commands.link,
      commands.quote,
      commands.code,
      commands.codeBlock,
      commands.fullscreen,
      timeCommand, // Custom Time command
      usesOvenCommand, // Custom Oven command
    ];
  }, [setText]); // setText is stable from useState, but included for clarity

  const insertImageMarkdown = (imageUuid) => {
    const imageUrl = `${apiClient.defaults.baseURL || ""}/static/recipe_images/${imageUuid}.jpg`;
    const imageMarkdown = `![Image](${imageUrl})`;
    
    // Try to insert at cursor position in the editor
    const textarea = document.querySelector('.w-md-editor-text-input');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = text.substring(0, start) + imageMarkdown + text.substring(end);
      setText(newText);
      // Set cursor position after inserted markdown
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + imageMarkdown.length, start + imageMarkdown.length);
      }, 0);
    } else {
      // Fallback: append to end
      setText(text + "\n" + imageMarkdown);
    }
  };

  useEffect(() => {
    if (step && step.recipe_step_uuid) {
      const currentStepUuid = step.recipe_step_uuid;
      
      // Only update text if this is a different step (UUID changed)
      // This prevents overwriting user edits when recipe refreshes after image operations
      if (currentStepUuid !== lastStepUuidRef.current) {
        setText(step.step_text || "");
        setStepNumber(step.step_number || "");
        lastStepUuidRef.current = currentStepUuid;
      } else {
        // Same step - only update step number if it changed, but preserve text (user might be editing)
        if (step.step_number !== undefined) {
          setStepNumber(step.step_number || "");
        }
      }
    } else {
      // For adding new step
      setText("");
      setStepNumber("");
      lastStepUuidRef.current = null;
    }
  }, [step?.recipe_step_uuid, step?.step_number]); // Only depend on step UUID and step_number, not text or recipe

  const handleImageUpload = async (file) => {
    if (!recipe || !recipe.recipe_uuid) {
      setError("Recipe is required to upload images");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await apiClient.post(
        `/recipes/${recipe.recipe_uuid}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setError("");
      
      // Update recipe images immediately so the image appears in the gallery right away
      // We need to update the recipe prop if onRecipeRefresh is available
      if (onRecipeRefresh) {
        // Fetch the updated recipe to get the new image
        await onRecipeRefresh();
      }
    } catch (err) {
      console.error("Failed to upload image:", err);
      setError(err.response?.data?.detail || "Failed to upload image");
    }
  };

  const handleImageDelete = async (imageUuid) => {
    if (!recipe) {
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this image? This will permanently remove it from the recipe and any references in the step text.")) {
      return;
    }
    
    // Remove markdown image references from step text BEFORE deleting
    // This ensures the cleaned text is in the editor when user saves
    if (text) {
      const imageUuidStr = String(imageUuid);
      // Pattern to match markdown image syntax with this image UUID
      // Matches: ![alt](url) where url contains the image_uuid
      // Handles both full size and thumbnail versions, with any base URL
      const pattern = new RegExp(
        `!\\[[^\\]]*\\]\\([^)]*${imageUuidStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:_thumb)?\\.jpg\\)`,
        'gi'
      );
      
      let cleanedText = text.replace(pattern, '');
      
      // Clean up multiple consecutive newlines that might result from removal
      cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n').trim();
      
      // Update the step text immediately
      if (cleanedText !== text) {
        setText(cleanedText);
      }
    }
    
    try {
      await apiClient.delete(`/recipes/${recipe.recipe_uuid}/images/${imageUuid}`);
      
      setError("");
      
      // Update the gallery immediately by removing the image from the recipe's images array
      if (onImageDeleted) {
        onImageDeleted(imageUuid);
      }
      
      // Don't refresh recipe after deleting image - we've already cleaned up the text locally
      // and updated the images array. Refreshing would cause a race condition where the old step text
      // might overwrite our cleaned version. The recipe will be refreshed when the step is saved anyway.
    } catch (err) {
      console.error("Failed to delete image:", err);
      setError(err.response?.data?.detail || "Failed to delete image. Please try again.");
    }
  };

  const handleSave = async () => {
    if (onSave) {
      const updates = { 
        step_text: text,
        step_number: stepNumber ? parseInt(stepNumber, 10) : undefined
      };
      
      if (step && step.recipe_step_uuid) {
        // Editing existing step
        await onSave(step.recipe_step_uuid, updates);
      } else {
        // Adding new step
        await onSave(null, updates);
      }
    }
    // Don't close immediately - let parent refresh recipe and update step prop
    // The parent will close the modal after refreshing
  };

  const handleClose = () => {
    setText("");
    setStepNumber("");
    setError("");
    onClose();
  };

  const handleDelete = () => {
    if (onDelete && step) {
      onDelete(step.recipe_step_uuid);
    }
    handleClose();
  };

  const isEditing = !!step;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{isEditing ? "Edit Step" : "Add Step"}</span>
          {isEditing && onDelete && (
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
              size="small"
            >
              Delete Step
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ mb: 2, p: 1, bgcolor: "error.light", color: "error.contrastText", borderRadius: 1 }}>
            {error}
          </Box>
        )}
        {step && step.recipe_step_uuid && (
          <TextField
            fullWidth
            label="Step Number"
            type="number"
            value={stepNumber}
            onChange={(e) => setStepNumber(e.target.value)}
            margin="normal"
            inputProps={{ min: 1 }}
            helperText="The order this step appears in the recipe"
          />
        )}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Step Instructions (Markdown)
          </Typography>
          <MDEditor
            value={text}
            onChange={setText}
            preview="edit"
            data-color-mode="light"
            commands={editorCommands}
          />
        </Box>
        
        {/* Gallery Section */}
        {recipe && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Gallery
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Click any image to insert it into the step. Upload new images to add them to the recipe gallery.
            </Typography>
            
            {/* Gallery Grid */}
            {recipe.images && recipe.images.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {recipe.images.map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.image_uuid}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="img"
                        src={`${apiClient.defaults.baseURL || ""}/static/recipe_images/${image.image_uuid}_thumb.jpg`}
                        alt="Recipe"
                        onClick={() => insertImageMarkdown(image.image_uuid)}
                        sx={{
                          width: "100%",
                          height: "auto",
                          borderRadius: 1,
                          aspectRatio: "16/10",
                          objectFit: "cover",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                        title="Click to insert image into markdown"
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleImageDelete(image.image_uuid);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {/* Upload button */}
            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCamera />}
              sx={{
                borderStyle: "dashed",
              }}
            >
              Upload Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleImageUpload(e.target.files[0]);
                  }
                }}
              />
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeStepEditModal;

